import type { VisibilityRange, DomainFilterValue } from '../types';

export interface TrackerDateRangeQuery {
  topicId?: string | null;
  range?: VisibilityRange;
  from?: string;
  to?: string;
  providerIds?: string[];
  brandKeys?: string[];
  sourceIds?: string[];
  domainFilter?: DomainFilterValue;
}

/** Standard table/dropdown filter shape (`ColumnFilterDto` on the backend). */
export interface ColumnFilter {
  filterModes: { mode: string; value: unknown }[];
  operator: 'and' | 'or';
}

/** POST body for range-based endpoints: dates top-level, dropdowns in `filters`. */
export interface TrackerRangeBody {
  range?: VisibilityRange;
  from_date?: string;
  to_date?: string;
  filters?: Record<string, ColumnFilter>;
}

export interface TrackerSourceInsightsBody extends TrackerRangeBody {
  page?: number;
  perPage?: number;
}

export function equalsFilter(values: string[]): ColumnFilter {
  return {
    filterModes: values.map(value => ({ mode: 'equals', value })),
    operator: 'or'
  };
}

export function buildTrackerRangeBody(
  options?: TrackerDateRangeQuery
): TrackerRangeBody {
  const body: TrackerRangeBody = {};
  if (!options) return body;

  if (options.range) {
    body.range = options.range;
    if (options.range === 'custom' && options.from && options.to) {
      body.from_date = options.from;
      body.to_date = options.to;
    }
  }

  const filters: Record<string, ColumnFilter> = {};
  if (options.topicId) filters.topicId = equalsFilter([options.topicId]);
  if (options.providerIds?.length) filters.providerIds = equalsFilter(options.providerIds);
  if (options.sourceIds?.length) filters.sourceIds = equalsFilter(options.sourceIds);
  if (options.domainFilter && options.domainFilter !== 'all') {
    filters.domain = equalsFilter([options.domainFilter]);
  }
  if (Object.keys(filters).length) body.filters = filters;
  return body;
}

/** Standard paginated list body: range scope + table filters (`search`, `status`, …) in one `filters` map. */
export interface TrackerListBody extends TrackerRangeBody {
  page?: number;
  perPage?: number;
  sort?: { column: string; order: 'asc' | 'desc' };
}

export function buildTrackerListBody(
  options: TrackerDateRangeQuery,
  tableFilters: Record<string, ColumnFilter> = {}
): TrackerListBody {
  const body = buildTrackerRangeBody(options);
  const filters = { ...body.filters, ...tableFilters };
  return Object.keys(filters).length ? { ...body, filters } : body;
}

export function containsFilter(value: string): ColumnFilter {
  return { filterModes: [{ mode: 'contains', value }], operator: 'and' };
}

export function buildSourceInsightsBody(
  options?: TrackerDateRangeQuery & { page?: number; perPage?: number }
): TrackerSourceInsightsBody {
  const body: TrackerSourceInsightsBody = buildTrackerRangeBody(options);
  if (options?.page != null) body.page = options.page;
  if (options?.perPage != null) body.perPage = options.perPage;
  return body;
}
